<template>
  <div class="clients-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">Clients</h1>
        <p class="page-sub">External clients who can view tasks shared with them</p>
      </div>
      <button class="btn btn-primary" @click="openForm(null)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Client
      </button>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else class="clients-table-wrap">
      <table class="clients-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in clients" :key="c.id">
            <td>
              <div class="member-name">
                <div class="member-avatar">{{ initials(c.name) }}</div>
                {{ c.name }}
              </div>
            </td>
            <td class="username-cell">{{ c.username }}</td>
            <td>
              <span :class="['status-dot', c.is_active ? 'active' : 'inactive']">
                {{ c.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td>
              <div class="row-actions">
                <button class="btn btn-sm btn-ghost" @click="openForm(c)">Edit</button>
                <button
                  class="btn btn-sm btn-ghost"
                  :class="{ 'danger-text': c.is_active }"
                  @click="toggleActive(c)"
                >{{ c.is_active ? 'Deactivate' : 'Activate' }}</button>
              </div>
            </td>
          </tr>
          <tr v-if="clients.length === 0">
            <td colspan="4" class="empty-cell">No external clients yet.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add / Edit modal -->
    <div v-if="showForm" class="modal-backdrop" @click.self="showForm = false">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ editing ? 'Edit Client' : 'Add Client' }}</h2>
          <button class="btn btn-icon modal-close" @click="showForm = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label>Full name *</label>
            <input v-model="form.name" class="form-control" required maxlength="120" />
          </div>
          <div class="form-group">
            <label>Username *</label>
            <input
              v-model="form.username"
              class="form-control"
              required
              maxlength="60"
              :disabled="!!editing"
              :style="editing ? 'opacity:.5;pointer-events:none' : ''"
              placeholder="no spaces, used to log in"
            />
          </div>
          <div class="form-group">
            <label>{{ editing ? 'New password (leave blank to keep)' : 'Password *' }}</label>
            <input
              v-model="form.password"
              type="password"
              class="form-control"
              :required="!editing"
              placeholder="min 8 characters"
            />
          </div>
          <p v-if="formError" class="form-error">{{ formError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="showForm = false">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Saving…' : (editing ? 'Save' : 'Add Client') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import api from '@/api'

export default {
  name: 'ClientsView',
  setup() {
    const clients   = ref([])
    const loading   = ref(true)
    const showForm  = ref(false)
    const editing   = ref(null)
    const saving    = ref(false)
    const formError = ref('')

    const form = reactive({ name: '', username: '', password: '' })

    async function load() {
      loading.value = true
      try { clients.value = (await api.getClients()).data || [] } catch {} finally { loading.value = false }
    }

    function openForm(client) {
      editing.value = client
      Object.assign(form, { name: client?.name || '', username: client?.username || '', password: '' })
      formError.value = ''
      showForm.value  = true
    }

    async function submitForm() {
      formError.value = ''
      saving.value    = true
      try {
        if (editing.value) {
          const payload = { name: form.name }
          if (form.password) payload.password = form.password
          await api.updateClient(editing.value.id, payload)
        } else {
          await api.createClient({ name: form.name, username: form.username, password: form.password })
        }
        showForm.value = false
        await load()
      } catch (e) {
        formError.value = e.message || 'Save failed'
      } finally {
        saving.value = false
      }
    }

    async function toggleActive(client) {
      const verb = client.is_active ? 'Deactivate' : 'Activate'
      if (!confirm(`${verb} ${client.name}?`)) return
      try { await api.updateClient(client.id, { is_active: !client.is_active }); await load() } catch {}
    }

    const initials = (name) => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

    onMounted(load)
    return { clients, loading, showForm, editing, form, saving, formError, openForm, submitForm, toggleActive, initials }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

.clients-table-wrap {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: auto;
}
.clients-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.clients-table th {
  text-align: left;
  padding: 12px 16px;
  color: var(--muted);
  font-weight: 500;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .05em;
  border-bottom: 1px solid var(--border);
}
.clients-table td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255,255,255,.04);
}
.clients-table tr:last-child td { border-bottom: none; }

.member-name { display: flex; align-items: center; gap: 10px; font-weight: 500; }
.member-avatar {
  width: 30px; height: 30px;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.username-cell { color: var(--muted); font-family: monospace; }

.status-dot { font-size: 12px; }
.status-dot.active   { color: var(--success); }
.status-dot.inactive { color: var(--muted); }

.row-actions { display: flex; gap: 6px; }
.danger-text { color: var(--danger); }
.empty-cell  { text-align: center; color: var(--muted); padding: 32px !important; }

.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  width: 100%; max-width: 440px;
  padding: 28px;
  box-shadow: var(--shadow);
}
.modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.modal-header h2 { font-size: 18px; font-weight: 700; }
.modal-close { color: var(--muted); }
.form-error {
  color: var(--danger); font-size: 13px; margin-bottom: 12px;
  padding: 8px 12px; background: rgba(239,68,68,.1); border-radius: 6px;
}
.modal-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border);
}
</style>
